class QuickViewModal extends HTMLElement {
  constructor() {
    super();
    this.currentRequest = null;
    this.isLoading = false;

    // Bind methods so `this` works correctly
    this.close = this.close.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
    this.onModalContentClick = this.onModalContentClick.bind(this);
    this.onCartDrawerMutate = this.onCartDrawerMutate.bind(this);

    // Add script to prevent Shop Pay Installments and Tolstoy from loading
    const script = document.createElement("script");
    script.textContent = `
      // Prevent Shop Pay Installments from initializing in quick view
      const originalDefine = customElements.define;
      customElements.define = function(name, constructor, options) {
        if (name === 'shopify-payment-terms' || name === 'tolstoy-stories') {
          // Return a no-op constructor for quick view
          return originalDefine.call(this, name, class extends HTMLElement {
            constructor() {
              super();
              this.style.display = 'none';
            }
          }, options);
        }
        return originalDefine.call(this, name, constructor, options);
      };
    `;
    document.head.appendChild(script);
  }

  connectedCallback() {
    // Cache references to key elements
    this.backdrop     = this.querySelector('.backdrop')
    this.closeBtn     = this.querySelector('.close-btn')
    this.modal        = this.querySelector('.quick_view_modal')
    this.modalContent = this.querySelector('.quick-add-modal')

    // Wire up event listeners
    this.closeBtn.addEventListener('click', this.close)
    this.backdrop.addEventListener('click', this.onBackdropClick)
    this.modalContent.addEventListener('click', this.onModalContentClick)
    window.addEventListener('quick-add:open', (e) => this.open(e.detail))
    document.addEventListener('keydown', this.handleEscape)

     // Watch for the cart drawer opening
     this.cartDrawer = document.querySelector('cart-drawer')
     if (this.cartDrawer) {
       // MutationObserver watches the `open` attribute on <cart-drawer>
       this.cartObserver = new MutationObserver(this.onCartDrawerMutate)
       this.cartObserver.observe(this.cartDrawer, { attributes: true, attributeFilter: ['open'] })
     }

     window.addEventListener('productGroupingInjected', () => {
      if (this.backdrop.hasAttribute('open')) this._setupFakeScrollbar();
    });
    
  }

  disconnectedCallback() {
    // Clean up listeners if the element is removed
    this.closeBtn.removeEventListener('click', this.close)
    this.backdrop.removeEventListener('click', this.onBackdropClick)
    this.modalContent.removeEventListener('click', this.onModalContentClick)
    document.removeEventListener('keydown', this.handleEscape)
    // Remove fake-scrollbar listeners if set
    if (this._removePickerListeners) this._removePickerListeners()
  }

  onBackdropClick(event) {
    // Only close if the click was directly on the backdrop, not inside the modal
    if (event.target === this.backdrop) {
      this.close()
    }
  }

  onModalContentClick(event) {
    // Don't navigate if clicking on interactive elements
    const interactiveSelectors = [
      'button',
      'input',
      'select',
      'textarea',
      'a',
      '[role="button"]',
      '[tabindex]',
      '.variant-picker',
      '.variant-picker *',
      '.buy-buttons',
      '.buy-buttons *',
      '.product-form',
      '.product-form *',
      '.quantity-selector',
      '.quantity-selector *',
      '.product-info__block-item[data-block-type="variant-picker"]',
      '.product-info__block-item[data-block-type="variant-picker"] *',
      '.product-info__block-item[data-block-type="buy-buttons"]',
      '.product-info__block-item[data-block-type="buy-buttons"] *'
    ];

    // Check if the clicked element or any of its parents match interactive selectors
    let element = event.target;
    while (element && element !== this.modalContent) {
      for (const selector of interactiveSelectors) {
        if (element.matches(selector)) {
          return; // Don't navigate, let the element handle the click
        }
      }
      element = element.parentElement;
    }

    // If we get here, the click wasn't on an interactive element
    // Navigate to the product page
    if (this.currentHandle) {
      window.location.href = `/products/${this.currentHandle}`;
    }
  }

  onCartDrawerMutate(mutations) {
    for (const m of mutations) {
      if (m.attributeName === 'open' && this.cartDrawer.hasAttribute('open')) {
        this.close()
        break
      }
    }
  }
  
  async open({ handle, variantId }) {
  // ✅ Properly check if content already injected
    if (this.currentHandle === handle && this.modalContent.innerHTML.trim() !== '') {
      console.log('[QuickView] Skipping re-injection for same product:', handle);
      this.backdrop.setAttribute("open", "");
      document.body.classList.add("modal-open");
      return;
    }

    // Cancel any existing request
    if (this.currentRequest) {
      this.currentRequest.abort();
      this.currentRequest = null;
    }

    // Set loading state
    this.isLoading = true;

    // Create new AbortController for this request
    const controller = new AbortController();
    this.currentRequest = controller;

    // 1) Fetch & inject
    const url = `/products/${handle}?view=product-info-ajax&variant=${variantId}&is-quick-view=true`;
    try {
      const res = await fetch(url, { signal: controller.signal });
      const html = await res.text();

      // Check if this request was cancelled
      if (!this.isLoading || this.currentRequest !== controller) return;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const main = doc.querySelector("#main-product-info");
      if (!main) throw new Error("Missing #main-product-info in response");

      // Remove any Shop Pay Installments and Yotpo elements before injecting
      const shopPayElements = main.querySelectorAll(
        "shopify-payment-terms, [data-shop-pay-installments], [data-shopify-payment-terms]"
      );
      shopPayElements.forEach((el) => el.remove());

      const yotpoElements = main.querySelectorAll("[data-yotpo-element]");
      yotpoElements.forEach((el) => el.remove());

      // Remove Tolstoy elements
      const tolstoyElements = main.querySelectorAll("tolstoy-stories");
      tolstoyElements.forEach((el) => el.remove());

      // Check if this request was cancelled BEFORE injecting content
      if (!this.isLoading || this.currentRequest !== controller) return;

      // Inject
      this.modalContent.innerHTML = ""; // Clear old content explicitly
      this.modalContent.innerHTML = main.innerHTML;
      console.log('[QuickView] Injected modal content for:', handle);
      this.currentHandle = handle;
    } catch (err) {
      // Only log error if it's not an abort error
      if (err.name !== "AbortError") {
        console.error("QuickViewModal fetch failed", err);
      }
      return;
    }

    // Final check if this request was cancelled or replaced
    if (!this.isLoading || this.currentRequest !== controller) return;

    // 2) Show backdrop
    this.backdrop.setAttribute("open", "");
    document.body.classList.add("modal-open");
    const swatchFieldset = this.modalContent.querySelector(
      ".variant-picker__option[data-option-name='Color']"
    );
    if (swatchFieldset) {
      swatchFieldset.classList.add("single-swatch");
    }

    // 3) Prepare for height anim

    this.modal.style.height = "0px";
    this.modalContent.classList.remove("is-visible");

    // 4) Double-RAF → expand to full height
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.setModalHeight();
      });
    });

    // 5) After expand → switch to auto + fade content in
    const onExpand = (e) => {
      if (e.propertyName !== "height") return;
      this.modal.removeEventListener("transitionend", onExpand);
      this.modal.style.height = "auto";
      this.modalContent.classList.add("is-visible");
      // NOW that the layout is final…
      this._setupFakeScrollbar();
    };
    this.modal.addEventListener("transitionend", onExpand);

    // 6) Focus
    this.closeBtn.focus();
  }

  setModalHeight() {
    const curH = this.modal.scrollHeight + "px";
    this.modal.style.height = curH;
  }

  close() {
    // Cancel any pending request
    if (this.currentRequest) {
      this.currentRequest.abort();
      this.currentRequest = null;
    }

    // Reset loading state
    this.isLoading = false;

    // 1) Fade out inner content
    this.modalContent.classList.remove("is-visible");
    this.setModalHeight();

    // 2) Wait 300ms, then collapse panel and clean up
    setTimeout(() => {
      // Clear modal content
      this.modalContent.innerHTML = "";
      this.currentHandle = null;
      console.log('[QuickView] Modal closed');

      // lock current height

      // force layout
      this.modal.offsetHeight;

      // animate back to zero
      this.modal.style.height = "0px";

      // 3) After collapse → hide backdrop & clean up
      const onCollapse = (ev) => {
        if (ev.propertyName !== "height") return;
        this.modal.removeEventListener("transitionend", onCollapse);

        // Remove open attribute
        this.backdrop.removeAttribute("open");
        document.body.classList.remove("modal-open");

        // Reset modal styles
        this.modal.style.height = "";
      };
      this.modal.addEventListener("transitionend", onCollapse);
    }, 300);
  }

  handleEscape(e) {
    if (e.key === 'Escape') this.close()
  }

  _setupFakeScrollbar() {
    const wrapper  = this.modalContent.querySelector('.picker-wrapper');
    const scroller = wrapper?.querySelector('[data-block-type="variant-picker"]');
    if (!wrapper || !scroller) return;
  
    // hide any old scrollbar
    wrapper.querySelector('.fake-scrollbar')?.remove();
    this._removePickerListeners?.();
  
    const ro = new ResizeObserver(() => {
      const { scrollHeight, clientHeight } = scroller;
      if (scrollHeight <= clientHeight) return;
      ro.disconnect();   // only do it once
  
      // … now inject your rail + thumb …
      const rail  = document.createElement('div');
      rail.className = 'fake-scrollbar';
      const thumb = document.createElement('div');
      thumb.className = 'thumb';
      rail.appendChild(thumb);
      wrapper.appendChild(rail);
  
      // sync function
      const sync = () => {
        const { scrollTop } = scroller;
        const thumbH = (clientHeight / scrollHeight) * clientHeight;
        const thumbT = (scrollTop       / scrollHeight) * clientHeight;
        thumb.style.height = `${thumbH}px`;
        thumb.style.top    = `${thumbT}px`;
      };
      scroller.addEventListener('scroll', sync);
      window.addEventListener('resize', sync);
      sync();
      this._removePickerListeners = () => {
        scroller.removeEventListener('scroll', sync);
        window.removeEventListener('resize', sync);
      };
    });
  
    // start observing the **content** size of your picker
    ro.observe(scroller);
  }
  
  
}

if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', QuickViewModal);
}
